import moment from 'jalali-moment';
import i18n from 'i18next';
import {
  DEFAULT_LOCALE,
  LOCALE_DATE_SETTINGS,
  isSupportedLanguage,
  type AppLanguage,
  type DateDisplayPreset
} from '@/locales/localeConfig';
import { toPersianDigit } from '@/helpers/utils';

type FormatUtcDateOptions = {
  language?: AppLanguage;
  timezone?: string;
  preset?: DateDisplayPreset;
  persianDigits?: boolean;
};

const getGregorianDateInTimezone = (isoUtc: string, timezone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date(isoUtc));

const getTimeInTimezone = (
  isoUtc: string,
  timezone: string,
  intlLocale: string
) =>
  new Intl.DateTimeFormat(intlLocale, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(new Date(isoUtc));

const resolveLanguage = (language?: AppLanguage): AppLanguage => {
  if (language) return language;
  if (isSupportedLanguage(i18n.language)) return i18n.language;
  return DEFAULT_LOCALE.language;
};

export const formatUtcDate = (
  utcIsoString: string | null | undefined,
  options: FormatUtcDateOptions = {}
): string => {
  if (!utcIsoString) return '';

  const date = new Date(utcIsoString);
  if (Number.isNaN(date.getTime())) return '';

  const language = resolveLanguage(options.language);
  const timezone = options.timezone ?? DEFAULT_LOCALE.timezone;
  const preset = options.preset ?? 'date';
  const settings = LOCALE_DATE_SETTINGS[language];
  const persianDigits = options.persianDigits ?? settings.persianDigits;

  try {
    if (preset === 'time') {
      const formatted = getTimeInTimezone(
        utcIsoString,
        timezone,
        settings.intlLocale
      );
      return persianDigits ? toPersianDigit(formatted) : formatted;
    }

    const gregorianInTimezone = getGregorianDateInTimezone(
      utcIsoString,
      timezone
    );
    const momentLocale = settings.calendar === 'jalali' ? 'fa' : 'en';
    let formatted = moment(gregorianInTimezone, 'YYYY-MM-DD')
      .locale(momentLocale)
      .format(settings.formats.date);

    if (preset === 'dateTime') {
      const time = getTimeInTimezone(
        utcIsoString,
        timezone,
        settings.intlLocale
      );
      formatted = `${time} ${formatted}`;
    }

    return persianDigits ? toPersianDigit(formatted) : formatted;
  } catch {
    return '';
  }
};

export default formatUtcDate;
